import { APP_VERSION, BUILD_SHA } from "../version";
import { Link, useNavigate } from "react-router-dom";
import { clearSession, getSession } from "../api";
import { PricingSettings } from "../components/PricingSettings";
import { ReserveSettings } from "../components/ReserveSettings";
import { type ThemePref, useTheme } from "../theme";
import { ScreenHeader, SectionTitle } from "../components/ui";

/**
 * Account tab - session info, theme picker, logout.
 *
 * The site_id row is informational today; a multi-site customer
 * (installer-customer with several installations) gets a real picker
 * here once /api/whoami learns to surface multiple sites (TODO v3).
 */
export function Account() {
  const navigate = useNavigate();
  const session = getSession();
  const { pref, setPref } = useTheme();

  function onLogout() {
    clearSession();
    navigate("/login");
  }

  return (
    <div className="tab-enter stagger px-4 pt-6 pb-28 max-w-3xl mx-auto flex flex-col gap-4 lg:px-8 lg:pt-10 lg:pb-12">
      <ScreenHeader title="Account" subtitle="Instellingen & profiel" />

      <div className="card p-4 flex items-center gap-4">
        <span className="w-12 h-12 rounded-full bg-accent-soft text-accent grid place-items-center text-lg font-bold uppercase shrink-0">
          {(session?.name || "?").slice(0, 1)}
        </span>
        <div className="min-w-0">
          <div className="text-base font-semibold truncate">{session?.name || "-"}</div>
          <div className="text-xs text-ink-mute truncate">locatie: {session?.site_id || "-"}</div>
        </div>
      </div>

      <PricingSettings />

      <ReserveSettings />

      <section className="flex flex-col gap-2">
        <SectionTitle>Slimme apparaten</SectionTitle>
        <Link to="/account/slim" className="card p-4 flex items-center justify-between gap-3">
          <span>
            <span className="block text-sm font-medium">Slimme momenten, apparaten en EV-laden</span>
            <span className="block text-xs text-ink-mute leading-relaxed">
              Boiler, vaatwasser, pomp of verwarming op de beste momenten laten draaien, en de evcc-koppeling. Standaard uit.
            </span>
          </span>
          <span className="text-ink-mute" aria-hidden="true">›</span>
        </Link>
      </section>

      <section className="flex flex-col gap-2">
        <SectionTitle>Weergave</SectionTitle>
        <div className="card overflow-hidden">
          <ThemeOption
            value="system"
            current={pref}
            onClick={setPref}
            label="Systeem"
            description="Volgt de instelling van je apparaat."
          />
          <Separator />
          <ThemeOption
            value="light"
            current={pref}
            onClick={setPref}
            label="Licht"
            description="Lichte achtergrond met donkere tekst."
          />
          <Separator />
          <ThemeOption
            value="dark"
            current={pref}
            onClick={setPref}
            label="Donker"
            description="Donkere achtergrond, fijn voor 's avonds."
          />
        </div>
      </section>

      <a
        href="https://help.mijnslimstroom.nl/"
        target="_blank"
        rel="noreferrer"
        className="card pressable py-3 text-center font-semibold block"
      >
        Hulp &amp; uitleg
      </a>

      <button
        onClick={onLogout}
        className="card pressable py-3 text-danger font-semibold hover:bg-danger-soft"
      >
        Uitloggen
      </button>
      <p className="text-center text-xs text-ink-mute pb-2">
        Mijn SlimStroom {APP_VERSION}
        {BUILD_SHA !== "dev" && <span className="opacity-60"> ({BUILD_SHA})</span>}
      </p>
    </div>
  );
}

function Separator() {
  return <div className="h-px bg-line" />;
}

interface ThemeOptionProps {
  value: ThemePref;
  current: ThemePref;
  onClick: (p: ThemePref) => void;
  label: string;
  description: string;
}
function ThemeOption({ value, current, onClick, label, description }: ThemeOptionProps) {
  const selected = current === value;
  return (
    <button
      onClick={() => onClick(value)}
      className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-card-soft"
    >
      <RadioDot selected={selected} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-ink-mute mt-0.5">{description}</div>
      </div>
    </button>
  );
}

function RadioDot({ selected }: { selected: boolean }) {
  return (
    <span
      className={`w-4 h-4 rounded-full border-2 grid place-items-center ${
        selected ? "border-accent" : "border-ink-mute"
      }`}
    >
      {selected && <span className="w-2 h-2 rounded-full bg-accent" />}
    </span>
  );
}
